/**
 * =========================================================================
 * MAIN LAYOUT COMPONENT
 * =========================================================================
 * 
 * This is the primary layout wrapper for the entire application.
 * It provides the consistent structure, navigation, and content areas.
 * 
 * Key Features:
 * - Responsive design with mobile/desktop layouts
 * - Conditional sidebar rendering based on auth state
 * - Dynamic page titles and descriptions from context
 * - Consistent spacing and visual hierarchy
 * - Background graphics and styling
 * 
 * Auth Integration:
 * - Uses auth context instead of localStorage
 * - Conditionally renders sidebar for authenticated users
 * - Proper loading states during auth checks
 * 
 * Layout Structure:
 * - Sidebar (1/6 width on desktop, hidden on mobile)
 * - Main content area (5/6 width on desktop, full on mobile)
 * - Header with page title and description
 * - Content outlet for route components
 * =========================================================================
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useMainPageTitle } from "../contexts/PageContext";
import { useAuth } from "../contexts/authContext";
import { useLanguage } from "../contexts/languageContext";
import Aside from "./aside";

/**
 * Layout Component
 * 
 * Main layout wrapper that provides consistent structure across all pages.
 * Handles responsive behavior and conditional rendering based on auth state.
 * 
 * Features:
 * - Responsive sidebar/main content layout
 * - Dynamic page titles from context
 * - Conditional sidebar rendering for authenticated users
 * - Consistent header styling with background graphics
 * - Mobile-responsive navigation
 * - Arabic/RTL language support with localized backgrounds
 * 
 * Background Images:
 * - English: background.png, header-background.png
 * - Arabic: background-ar.png, header-background-ar.png
 * - Automatically switches based on current language setting
 * 
 * Layout Behavior:
 * - Desktop: Sidebar + main content (split layout)
 * - Mobile: Stacked layout with mobile header
 * - Authenticated: Shows sidebar navigation
 * - Unauthenticated: Content only (auth pages)
 */
function Layout({ children }) {
  // Get page title and description from context
  const { title, description } = useMainPageTitle();
  
  // Get authentication state from context
  const { isAuthenticated, user } = useAuth();
  
  // Get language context
  const { currentLanguage } = useLanguage();
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Notification state for mobile button
  const [hasNotifications, setHasNotifications] = useState(false);
  
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  // Close mobile menu
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Get appropriate header background based on language
  const getHeaderBackground = () => {
    if (currentLanguage === 'ar') {
      return require("../assets/images/header-background-ar.png");
    }
    return require("../assets/images/header-background.png");
  };

  // Listen for notifications to show on mobile button
  useEffect(() => {
    const checkNotifications = () => {
      const unreadNotifications = parseInt(localStorage.getItem('unreadNotifications')) || 0;
      const unreadMessages = parseInt(localStorage.getItem('unreadMessages')) || 0;
      setHasNotifications(unreadNotifications > 0 || unreadMessages > 0);
    };

    // Check initially
    checkNotifications();

    // Listen for updates
    const handleUpdate = () => checkNotifications();
    window.addEventListener('unreadCountUpdate', handleUpdate);
    window.addEventListener('unreadMessagesUpdate', handleUpdate);
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener('unreadCountUpdate', handleUpdate);
      window.removeEventListener('unreadMessagesUpdate', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, []);

  return (
    <>
      <div className="flex items-stretch justify-center min-h-screen py-4 lg:py-10 lg:overflow-hidden max-h-screen lg:h-screen lg:flex-row flex-col lg:mx-2 main-layout-container">
        
        {/* ==================== DESKTOP SIDEBAR ==================== */}
        {/* Sidebar - only visible on desktop and when user is authenticated */}
        <div className="w-80 flex-shrink-0 hidden lg:block z-20 h-full mx-2 lg:mx-5 sidebar-container">
          {isAuthenticated && user && <Aside />}
        </div>

        {/* ==================== MOBILE HEADER ==================== */}
        {/* Mobile header - only visible on mobile devices */}
        {isAuthenticated && user && (
          <div className="w-full block lg:hidden my-2 px-4 mobile-header">
            <div className="flex items-center justify-between">
              {/* Site Logo */}
              <div className="flex items-center mobile-logo">
                <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
                  <img
                    src={require("../assets/images/logo.png")}
                    alt="Nashi Logo"
                    className="h-10 w-auto"
                  />
                </Link>
              </div>
              
              {/* Special Mobile Menu Button */}
              <button 
                onClick={toggleMobileMenu}
                className="relative p-3 bg-gradient-to-r from-[#F15213] to-[#FF6B35] text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 active:scale-95 group mobile-menu-button"
                aria-label="Toggle mobile menu"
              >
                {/* Animated hamburger menu */}
                <div className="flex flex-col space-y-1">
                  <div className={`w-5 h-0.5 bg-white rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'transform rotate-45 translate-y-1.5' : ''}`}></div>
                  <div className={`w-5 h-0.5 bg-white rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></div>
                  <div className={`w-5 h-0.5 bg-white rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'transform -rotate-45 -translate-y-1.5' : ''}`}></div>
                </div>
                
                {/* Notification dot - only show when there are notifications */}
                {hasNotifications && (
                  <div className="notification-dot absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"></div>
                )}
                
                {/* Subtle glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#F15213] to-[#FF6B35] rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-200 blur-xl"></div>
              </button>
            </div>
          </div>
        )}

        {/* ==================== MOBILE MENU OVERLAY ==================== */}
        {/* Mobile menu overlay - shown when menu is open */}
        {isMobileMenuOpen && isAuthenticated && user && (
          <div className="fixed inset-0 z-50 lg:hidden">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={closeMobileMenu}
            ></div>
            
            {/* Mobile sidebar */}
            <div className="fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] transform transition-transform mobile-sidebar">
              <Aside onClose={closeMobileMenu} isMobile={true} />
            </div>
          </div>
        )}

        {/* ==================== MAIN CONTENT AREA ==================== */}
        <main className="flex-1 w-full h-full relative z-10 min-h-[90vh] min-w-0 layout-main">
          
          {/* Background Layer - Decorative element for desktop */}
          <div className="back-page absolute top-[50%] left-[-50px] w-full h-[90%] transform -translate-y-1/2 rounded-[2vw] bg-[#eeeeee45] lg:block hidden"></div>
          
          {/* Main Content Container */}
          <div className="front-page main-content-container bg-white lg:rounded-l-[3vw] rounded-lg relative overflow-x-hidden lg:max-h-[95vh] h-full min-h-[90vh] w-full shadow-lg mx-2 lg:mx-0">
            <div className="container mx-auto px-2 lg:px-0 layout-container">
              
              {/* ==================== PAGE HEADER ==================== */}
              {/* Only show header on desktop, mobile header is handled above */}
              <header style={{paddingLeft:"25px"}} className="hidden lg:flex items-center justify-between w-full h-[90px] my-10 overflow-hidden  page-header">
                
                {/* Page Title and Description */}
                <div className="mb-9 lg:mb-0 z-20 relative">
                  <h1 className="text-2xl lg:text-3xl xl:text-4xl font-[700] mb-2 text-gray-800 page-title">{title}</h1>
                  <p className="text-lg lg:text-xl py-3 text-gray-500 page-description">{description}</p>
                </div>
                
                {/* Header Background Graphic */}
                <div
                  className="lg:w-[75%] w-full lg:h-[102px] h-[50px] absolute lg:right-[-50px]  z-10 header-background"
                  style={{
                    backgroundImage: `url(${getHeaderBackground()})`,
                    backgroundPosition: "center",
                    backgroundSize: "100% 100%",
                    backgroundRepeat: "no-repeat",
                    backgroundOrigin: "center",
                    backgroundPositionX: "center",
                    backgroundPositionY: "center",
                  }}
                  aria-hidden="true"
                ></div>
              </header>

              {/* Mobile Title and Description - only show on mobile */}
              {isAuthenticated && user && (
                <div className="block lg:hidden px-4 py-4 border-b border-gray-100">
                  <h1 className="text-2xl sm:text-3xl font-[700] mb-2 text-gray-800 page-title">{title}</h1>
                  <p className="text-base text-gray-500 page-description">{description}</p>
                </div>
              )}
              
              {/* ==================== PAGE CONTENT ==================== */}
              {/* Content area where route components are rendered */}
              <div className="px-2 lg:px-4 relative z-20  content-area">
                {children}
              </div>
            </div>
          </div>
        </main>
    </div>
    </>
  );
}

export default Layout;
